import { Logger } from '@modules/logger/logger';
import { Singleton } from '@visiba/instantiation';
import { sanitizedInjectHTML } from '@visiba/react-std';
import { delay, Timer } from '@visiba/std';
import { v4 } from 'uuid';

export type Assertiveness = 'polite' | 'assertive';

interface AnnounceMessage {
	id: string;
	assertiveness: Assertiveness;
	message: string;
	abortController: AbortController;
}

@Singleton()
export class LiveAnnouncerController {
	public politeContainer: HTMLDivElement;
	public assertiveContainer: HTMLDivElement;
	private queueIsRunning = false;
	private clearMessageTimeout = new Timer();
	private announceMessageQueue: AnnounceMessage[] = [];
	private lastElement: HTMLElement | null = null;

	constructor() {
		this.politeContainer = this.createElement('polite');
		this.assertiveContainer = this.createElement('assertive');
	}

	public announce(message: string, assertiveness: Assertiveness = 'polite'): RemoveThisMessageFromDom {
		const announceMessage: AnnounceMessage = {
			id: v4(),
			assertiveness,
			message,
			abortController: new AbortController(),
		};
		this.announceMessageQueue.push(announceMessage);

		this.render();

		return () => {
			announceMessage.abortController.abort();
			this.removeMessageIfExisting(announceMessage.id);
		};
	}

	public getContainer(assertiveness: Assertiveness): HTMLElement {
		return assertiveness === 'assertive' ? this.assertiveContainer : this.politeContainer;
	}

	private async render(): Promise<void> {
		if (this.queueIsRunning) return;

		this.queueIsRunning = true;
		this.clearMessageTimeout.flush();

		do {
			this.lastElement?.remove();

			const currentMessage = this.announceMessageQueue.shift();
			if (currentMessage == null) break;

			if (currentMessage.abortController.signal.aborted) {
				this.removeMessageIfExisting(currentMessage.id);

				continue;
			}

			const container = this.getContainer(currentMessage.assertiveness);

			const wrapper = document.createElement('div');

			wrapper.innerHTML = sanitizedInjectHTML(currentMessage.message).__html;
			wrapper.setAttribute('data-id', currentMessage.id);
			this.lastElement = wrapper;

			container.appendChild(wrapper);
			Logger.assert(Logger.Level.verbose, `[LiveAnnouncer][${currentMessage.assertiveness}] ${currentMessage.message}`);

			// Delay the next message so screen readers have time to perceive the change
			await delay(100);
		} while (this.announceMessageQueue.length !== 0);

		// Once settle clear message after a timeout so there are no messages
		// that could be read later.
		this.queueIsRunning = false;
	}

	private createElement(assertiveness: Assertiveness): HTMLDivElement {
		const element = document.createElement('div');

		element.setAttribute('role', assertiveness === 'polite' ? 'log' : 'log');
		element.setAttribute('aria-live', assertiveness);
		element.setAttribute('aria-relevant', 'additions');

		element.style.border = '0';
		element.style.clip = 'rect(1px, 1px, 1px, 1px)';
		element.style.clipPath = 'inset(50%)';
		element.style.height = '1px';
		element.style.margin = '-1px';
		element.style.overflow = 'hidden';
		element.style.padding = '0';
		element.style.position = 'absolute';
		element.style.width = '1px';
		element.style.wordWrap = 'normal !important';

		document.body.appendChild(element);

		return element;
	}

	private removeMessageIfExisting(id: string): void {
		const query = `[data-id="${id}"]`;
		const element = this.politeContainer.querySelector(query) ?? this.politeContainer.querySelector(query);
		element?.remove();
	}
}

type RemoveThisMessageFromDom = () => void;
